import React, { useEffect, useState, useContext } from 'react'
import {
  Box,
  Button,
  IconButton,
  Grid,
  GridItem,
  Heading,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  UnorderedList
} from '@chakra-ui/react'
import { connection, RoomCode } from '../../renderer/roomConnector'
import { RoomSettingsContext } from '../+RoomSettingsContext'
import { ErrorModal, ScreenSizeErrorModal } from '../../components/Modals'
import { DeleteIcon } from '@chakra-ui/icons'
import StickyNotes from '../../components/StickyNote'
import { activities, universalRoomOptions } from '../../activities'
import SideNav from '../../components/SideNav'
import '@fontsource/kalam'

interface itemData {
  id: string
  name: string
  description: string
}
interface featuredItemData {
  id: string
  name: string
  description: string
  type: string
}

let isConnecting = false

const initialRoomSettings = {
  activity: activities.startStopContinue,
  info: (
    <>
      <Heading size="sm" mb="0">
        Host
      </Heading>
      <UnorderedList>
        {activities.startStopContinue.hostInfo.map(
          (text: string, index: number) => {
            return <ListItem key={`hostInfo-${index}`}>{text}</ListItem>
          }
        )}
      </UnorderedList>
      <Heading size="sm" mb="0" mt="20px">
        Audience
      </Heading>
      <UnorderedList>
        {activities.startStopContinue.audienceInfo.map(
          (text: string, index: number) => {
            return <ListItem key={`audienceInfo-${index}`}>{text}</ListItem>
          }
        )}
      </UnorderedList>
    </>
  ),
  options: {
    ...universalRoomOptions
  }
}
const Page: React.FC = () => {
  const [roomCode, setRoomCode] = useState('')
  const { setRoomSettings } = useContext(RoomSettingsContext)
  const [featuredPostIt, setFeaturedPostIt] = useState<featuredItemData | null>(
    null
  )
  const [startItems, setStartItems] = useState<itemData[]>([])
  const [stopItems, setStopItems] = useState<itemData[]>([])
  const [continueItems, setContinueItems] = useState<itemData[]>([])
  const [csvData, setCsvData] = useState<string[][]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const updateItems = (message: {
    id: string
    clientId: string
    description: string
    type: string
  }): void => {
    switch (message.type) {
      case 'start':
        setStartItems((prevValue) => [
          ...prevValue,
          {
            id: message.id + new Date().toISOString(),
            name: message.clientId,
            description: message.description
          }
        ])
        break
      case 'stop':
        setStopItems((prevValue) => [
          ...prevValue,
          {
            id: message.id + new Date().toISOString(),
            name: message.clientId,
            description: message.description
          }
        ])
        break
      case 'continue':
        setContinueItems((prevValue) => [
          ...prevValue,
          {
            id: message.id + new Date().toISOString(),
            name: message.clientId,
            description: message.description
          }
        ])
        break
      default:
    }
  }

  const triggerAction = (message: any): void => {
    updateItems(message)
  }

  const triggerEnter = (message: any): void => {
    // if (ref?.current?.isRunning) {
    //     ref.current.stop();
    // }
  }

  const SSCCallback = (results: any): void => {
    if (results !== false) {
      setRoomCode(results.roomCode)
    } else {
      setErrorMessage('Failed to connect, try refreshing the window')
    }
  }

  useEffect(() => {
    setRoomSettings(initialRoomSettings)
    if (!isConnecting) {
      isConnecting = true
      connection('ssc', SSCCallback, { triggerAction, triggerEnter })
    }
  }, [])

  useEffect(() => {
    const newCSVData = [['id', 'name', 'note', 'type']]
    startItems.forEach((item) => {
      newCSVData.push([item.id, item.name, item.description, 'start'])
    })
    stopItems.forEach((item) => {
      newCSVData.push([item.id, item.name, item.description, 'stop'])
    })
    continueItems.forEach((item) => {
      newCSVData.push([item.id, item.name, item.description, 'continue'])
    })
    setCsvData(newCSVData)
  }, [startItems, stopItems, continueItems])

  const remove = (index: number, type: string): void => {
    switch (type) {
      case 'start':
        startItems.splice(index, 1)
        setStartItems([...startItems])
        break
      case 'stop':
        stopItems.splice(index, 1)
        setStopItems([...stopItems])
        break
      case 'continue':
        continueItems.splice(index, 1)
        setContinueItems([...continueItems])
        break
      default:
    }
  }

  const zoom = (index: number, type: string): void => {
    let tempFeaturedPostIt = null
    let newFeaturedPostIt = null

    switch (type) {
      case 'start':
        tempFeaturedPostIt = startItems[index]
        break
      case 'stop':
        tempFeaturedPostIt = stopItems[index]
        break
      case 'continue':
        tempFeaturedPostIt = continueItems[index]
        break
      default:
        tempFeaturedPostIt = null
    }

    if (tempFeaturedPostIt != null) {
      newFeaturedPostIt = {
        id: tempFeaturedPostIt.id,
        name: tempFeaturedPostIt !== null ? tempFeaturedPostIt.name : '',
        description: tempFeaturedPostIt.description,
        type
      }
    }

    setFeaturedPostIt(newFeaturedPostIt)
  }

  return (
    <Box sx={styles.container}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" />
      <Grid templateColumns="1fr 1fr 1fr" sx={styles.grid}>
        <GridItem px="1vw">
          <Heading textAlign="center">
            <Box
              mb="0.5vh"
              borderRadius="10vw"
              color="green.500"
              letterSpacing="-0.005em"
              fontWeight={800}
              fontSize="min(64px, 5vh)"
              transform="rotate(-5deg)"
              pos="relative"
              display="inline-block"
              sx={{
                '&:before': {
                  WebkitTextStrokeWidth: '0.03em',
                  WebkitTextStrokeColor: '000000aa',
                  content: '"Start"',
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  zIndex: '-1'
                },
                '&:after': {
                  content: '"Start"',
                  position: 'absolute',
                  top: '0.125em',
                  left: '0',
                  zIndex: '-1',
                  textShadow: 'none',
                  transition: 'top 250ms ease',
                  backgroundImage: `
                    linear-gradient(
                      45deg,
                      transparent 45%,
                      #000000aa 45%,
                      #000000aa 55%,
                      transparent 0
                      )`,
                  backgroundSize: '.05em .05em',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',

                  animation: 'shad-anim 15s linear infinite'
                }
              }}
            >
              Start
            </Box>
          </Heading>

          <Grid templateColumns="1fr 1fr" gap="10px" pb="300px">
            {startItems.map((item, key) => {
              return (
                <PostIt
                  key={key}
                  index={key}
                  remove={remove}
                  zoom={zoom}
                  type="start"
                  item={item}
                  isZoomed={featuredPostIt?.id === item.id}
                />
              )
            })}
          </Grid>
        </GridItem>
        <GridItem
          px="1vw"
          sx={{
            borderLeft: '1px solid lightgray',
            borderRight: '1px solid lightgray'
          }}
        >
          <Heading textAlign="center">
            {' '}
            <Box
              mb="0.5vh"
              borderRadius="10vw"
              color="#ED574F"
              letterSpacing="-0.005em"
              fontWeight={800}
              fontSize="min(64px, 5vh)"
              transform="rotate(-5deg)"
              pos="relative"
              display="inline-block"
              sx={{
                '&:before': {
                  WebkitTextStrokeWidth: '0.03em',
                  WebkitTextStrokeColor: '000000aa',
                  content: '"Stop"',
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  zIndex: '-1'
                },
                '&:after': {
                  content: '"Stop"',
                  position: 'absolute',
                  top: '0.125em',
                  left: '0',
                  zIndex: '-1',
                  textShadow: 'none',
                  transition: 'top 250ms ease',
                  backgroundImage: `
                    linear-gradient(
                      45deg,
                      transparent 45%,
                      #000000aa 45%,
                      #000000aa 55%,
                      transparent 0
                      )`,
                  backgroundSize: '.05em .05em',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',

                  animation: 'shad-anim 15s linear infinite'
                }
              }}
            >
              Stop
            </Box>
          </Heading>
          <Grid templateColumns="1fr 1fr" gap="10px">
            {stopItems.map((item, key) => {
              return (
                <PostIt
                  key={key}
                  index={key}
                  remove={remove}
                  zoom={zoom}
                  type="stop"
                  item={item}
                  isZoomed={featuredPostIt?.id === item.id}
                />
              )
            })}
          </Grid>
        </GridItem>
        <GridItem px="1vw">
          <Heading textAlign="center">
            {' '}
            <Box
              mb="0.5vh"
              borderRadius="10vw"
              color="yellow.400"
              letterSpacing="-0.005em"
              fontWeight={800}
              fontSize="min(64px, 5vh)"
              transform="rotate(-5deg)"
              pos="relative"
              display="inline-block"
              sx={{
                '&:before': {
                  WebkitTextStrokeWidth: '0.03em',
                  WebkitTextStrokeColor: '000000aa',
                  content: '"Continue"',
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  zIndex: '-1'
                },
                '&:after': {
                  content: '"Continue"',
                  position: 'absolute',
                  top: '0.125em',
                  left: '0',
                  zIndex: '-1',
                  textShadow: 'none',
                  transition: 'top 250ms ease',
                  backgroundImage: `
                    linear-gradient(
                      45deg,
                      transparent 45%,
                      #000000aa 45%,
                      #000000aa 55%,
                      transparent 0
                      )`,
                  backgroundSize: '.05em .05em',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',

                  animation: 'shad-anim 15s linear infinite'
                }
              }}
            >
              Continue
            </Box>
          </Heading>
          <Grid templateColumns="1fr 1fr" gap="10px">
            {continueItems.map((item, key) => {
              return (
                <PostIt
                  key={key}
                  index={key}
                  remove={remove}
                  zoom={zoom}
                  type="continue"
                  item={item}
                  isZoomed={featuredPostIt?.id === item.id}
                />
              )
            })}
          </Grid>
        </GridItem>
      </Grid>

      <Modal
        size="3xl"
        isOpen={featuredPostIt !== null}
        onClose={() => {
          setFeaturedPostIt(null)
        }}
      >
        <ModalOverlay />
        <ModalContent
          sx={{
            bg: 'transparent',
            boxShadow: 'none'
          }}
        >
          <PostItZoom
            item={featuredPostIt != null ? featuredPostIt : null}
            close={() => {
              setFeaturedPostIt(null)
            }}
            modalDelete={() => {
              if (featuredPostIt !== null) {
                switch (featuredPostIt.type) {
                  case 'start':
                    setStartItems(
                      startItems.filter((item) => item.id !== featuredPostIt.id)
                    )
                    break
                  case 'stop':
                    setStopItems(
                      stopItems.filter((item) => item.id !== featuredPostIt.id)
                    )
                    break
                  case 'continue':
                    setContinueItems(
                      continueItems.filter(
                        (item) => item.id !== featuredPostIt.id
                      )
                    )
                    break
                  default:
                }
                setFeaturedPostIt(null)
              }
            }}
          />
        </ModalContent>
      </Modal>
      <ErrorModal
        isOpen={errorMessage !== null}
        onClose={() => {
          setErrorMessage(null)
        }}
      >
        {errorMessage}
      </ErrorModal>
      <ScreenSizeErrorModal />

      <RoomCode roomCode={roomCode} left />
      <SideNav
        download={{
          data: csvData,
          filename: `start-stop-continue-${
            new Date().toISOString().split('T')[0]
          }.csv`
        }}
      />
    </Box>
  )
}

const styles = {
  container: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  },
  grid: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  },
  csv: {
    position: 'fixed',
    bottom: '0.25em',
    left: '0.25em',
    fontSize: '25px',
    zIndex: 1000
  }
}

interface PostItData {
  item: {
    name: string
    description: string
  }
  index: number
  type: string
  remove: (index: number, type: string) => void
  zoom: (index: number, type: string) => void
  isZoomed: boolean
}

const PostIt: React.FC<PostItData> = (props) => {
  const maxAngle = 3
  const [rotations] = useState(0 - maxAngle + Math.random() * 2 * maxAngle)
  const { item, index, type, remove, zoom, isZoomed } = props
  const bgColors = isZoomed
    ? 'grey'
    : {
        start: 'green',
        stop: 'red',
        continue: 'yellow'
      }[type]

  return (
    <StickyNotes
      as="button"
      onClick={() => {
        zoom(index, type)
      }}
      color={bgColors}
      sx={{
        transform: `rotate(${rotations}deg)`
      }}
    >
      <Box
        sx={{
          overflow: 'hidden',
          maxH: '9em',
          display: '-webkit-box',
          WebkitLineClamp: 5,
          WebkitBoxOrient: 'vertical',
          fontSize: '1vw',
          lineHeight: '1.25em',
          textAlign: 'left',
          fontFamily: 'Kalam, cursive',
          mb: '2em',
          h: '100%'
        }}
      >
        {item.description}
      </Box>
      <Box pos="absolute" fontFamily="Kalam, cursive" left="5%" bottom="5%">
        {item.name !== '' ? item.name : 'Anonymous'}
      </Box>
      <IconButton
        aria-label="Delete Note"
        icon={<DeleteIcon />}
        sx={{
          pos: 'absolute',
          right: '5%',
          top: '0px',
          bg: 'none',
          fontSize: '18px',
          p: '5px',
          _hover: {
            bg: 'none'
          }
        }}
        onClick={() => {
          remove(index, type)
        }}
      />
    </StickyNotes>
  )
}

interface PostItZoomData {
  item: {
    name: string
    description: string
    type: string
  } | null
  close: () => void
  modalDelete: () => void
}

const PostItZoom: React.FC<PostItZoomData> = (props) => {
  const { item, close, modalDelete } = props

  if (item === null) {
    return <></>
  }

  const bgColors = {
    start: 'green.300',
    stop: 'red.300',
    continue: 'yellow.300'
  }[item.type]

  return (
    <Box>
      <Box
        sx={{
          bg: bgColors,
          boxShadow: '2px 2px 5px rgba(0,0,0,0.5)',
          aspectRatio: '1',
          width: '90%',
          mx: 'auto',
          mb: '20px',
          p: '20px',
          pos: 'relative',
          fontFamily: 'Kalam, cursive'
        }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            maxH: '9em',
            display: '-webkit-box',
            WebkitLineClamp: 6,
            WebkitBoxOrient: 'vertical',
            fontSize: '36px',
            lineHeight: '1.25em',
            textAlign: 'left'
          }}
        >
          {item.description}
        </Box>
        <Box fontSize="36px" pos="absolute" left="10px" bottom="5px">
          {item.name !== '' ? item.name : 'Anonymous'}
        </Box>
        <Button
          sx={{
            pos: 'absolute',
            right: '0px',
            top: '0px',
            bg: 'none',
            fontSize: '18px',
            p: '5px',
            _hover: {
              bg: 'none'
            }
          }}
          onClick={() => {
            close()
          }}
        >
          X
        </Button>
        <IconButton
          aria-label="Delete Note"
          icon={<DeleteIcon />}
          sx={{
            pos: 'absolute',
            right: '0px',
            bottom: '0px',
            bg: 'none',
            fontSize: '18px',
            p: '5px',
            _hover: {
              bg: 'none'
            }
          }}
          onClick={modalDelete}
        />
      </Box>
    </Box>
  )
}

export { Page }
